import React, { useRef, useState, useMemo } from 'react';
import { useProdutos, useCriarProduto, useAtualizarProduto, useAlternarStatusProduto, useUploadFotoProduto, useCategoriasProduto } from '../../hooks/useProdutos';
import { useIngredientes } from '../../hooks/useIngredientes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Produto } from '../../types';
import Button from '../../components/Common/Button';
import SearchBar from '../../components/Common/SearchBar';
import Input from '../../components/Common/Input';
import Textarea from '../../components/Common/Textarea';
import { validateRequired, validatePositiveNumber, validateInteger, validateMinLength, combineValidations } from '../../utils/validations';

interface ProdutoFormData {
  nome: string;
  descricao: string;
  precoUnitario: string;
  custo: string;
  tempoPreparoMinutos: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

const getImagemUrl = (urlFoto?: string | null): string | null => {
  if (!urlFoto) return null;
  return `${API_BASE_URL}${urlFoto}`;
};

const Produtos: React.FC = () => {
  usePageTitle('Produtos');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');
  const { data: produtos, isLoading } = useProdutos(!mostrarInativos);
  const { data: ingredientes, isLoading: carregandoIngredientes } = useIngredientes();
  const { data: categorias } = useCategoriasProduto();
  const { mutate: criarProduto, isPending: criando } = useCriarProduto();
  const { mutate: atualizarProduto, isPending: atualizando } = useAtualizarProduto();
  const { mutate: alternarStatus } = useAlternarStatusProduto();
  const { mutate: uploadFoto, isPending: enviandoFoto } = useUploadFotoProduto();

  const [mostrarModal, setMostrarModal] = useState(false);
  const [produtoEditando, setProdutoEditando] = useState<Produto | null>(null);
  const [formData, setFormData] = useState<ProdutoFormData>({
    nome: '',
    descricao: '',
    precoUnitario: '',
    custo: '',
    tempoPreparoMinutos: '',
  });
  const [ingredientesQuantidades, setIngredientesQuantidades] = useState<{ [key: number]: number }>({});
  const [ingredientesDisplay, setIngredientesDisplay] = useState<{ [key: number]: string }>({});
  const [sobEncomenda, setSobEncomenda] = useState<boolean>(false);
  const [categoriaProdutoId, setCategoriaProdutoId] = useState<number | null>(null);
  const [fotoSelecionada, setFotoSelecionada] = useState<File | null>(null);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);
  const inputFotoRef = useRef<HTMLInputElement>(null);
  const [sabores, setSabores] = useState<string[]>([]);
  const [novoSabor, setNovoSabor] = useState('');

  const produtosFiltrados = useMemo(() => {
    if (!produtos) return [];
    if (!termoBusca.trim()) return produtos;

    const termo = termoBusca.toLowerCase();
    return produtos.filter(p =>
      p.nome.toLowerCase().includes(termo) ||
      p.descricao?.toLowerCase().includes(termo)
    );
  }, [produtos, termoBusca]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const abrirModalNovo = () => {
    setProdutoEditando(null);
    setFormData({ nome: '', descricao: '', precoUnitario: '', custo: '', tempoPreparoMinutos: '' });
    setIngredientesQuantidades({});
    setIngredientesDisplay({});
    setSobEncomenda(false);
    setCategoriaProdutoId(null);
    setFotoSelecionada(null);
    setPreviewFoto(null);
    setSabores([]);
    setNovoSabor('');
    setMostrarModal(true);
  };

  const abrirModalEditar = (produto: Produto) => {
    setProdutoEditando(produto);
    setFormData({
      nome: produto.nome,
      descricao: produto.descricao,
      precoUnitario: String(produto.precoUnitario),
      custo: String(produto.custo),
      tempoPreparoMinutos: String(produto.tempoPreparoMinutos),
    });
    const quantidades: { [key: number]: number } = {};
    const display: { [key: number]: string } = {};
    if (produto.ingredientes && produto.ingredientes.length > 0) {
      produto.ingredientes.forEach(ing => {
        quantidades[ing.ingredienteId] = ing.quantidade;
        display[ing.ingredienteId] = String(ing.quantidade);
      });
    }
    setIngredientesQuantidades(quantidades);
    setIngredientesDisplay(display);
    setSobEncomenda(produto.sobEncomenda);
    setCategoriaProdutoId(produto.categoriaProdutoId ?? null);
    setFotoSelecionada(null);
    setPreviewFoto(getImagemUrl(produto.urlFoto));
    setSabores(produto.sabores ?? []);
    setNovoSabor('');
    setMostrarModal(true);
  };

  const fecharModal = () => {
    setMostrarModal(false);
    setProdutoEditando(null);
    setFormData({ nome: '', descricao: '', precoUnitario: '', custo: '', tempoPreparoMinutos: '' });
    setIngredientesQuantidades({});
    setIngredientesDisplay({});
    setSobEncomenda(false);
    setCategoriaProdutoId(null);
    setFotoSelecionada(null);
    setPreviewFoto(null);
    setSabores([]);
    setNovoSabor('');
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFotoSelecionada(file);
    setPreviewFoto(URL.createObjectURL(file));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const ingredientes = Object.entries(ingredientesQuantidades)
      .filter(([_, quantidade]) => quantidade > 0)
      .map(([ingredienteId, quantidade]) => ({ ingredienteId: parseInt(ingredienteId), quantidade }));

    const precoUnitario = parseFloat(formData.precoUnitario.replace(',', '.')) || 0;
    const custo = parseFloat(formData.custo.replace(',', '.')) || 0;
    const tempoPreparoMinutos = parseInt(formData.tempoPreparoMinutos) || 0;

    if (produtoEditando) {
      atualizarProduto(
        { ...produtoEditando, nome: formData.nome, descricao: formData.descricao, precoUnitario, custo, tempoPreparoMinutos, sobEncomenda, categoriaProdutoId, ingredientes, sabores },
        {
          onSuccess: () => {
            if (fotoSelecionada) {
              uploadFoto({ id: produtoEditando.id, foto: fotoSelecionada }, { onSettled: () => fecharModal() });
            } else {
              fecharModal();
            }
          },
        }
      );
    } else {
      criarProduto(
        { nome: formData.nome, descricao: formData.descricao, precoUnitario, custo, tempoPreparoMinutos, sobEncomenda, categoriaProdutoId, ingredientes, sabores },
        {
          onSuccess: (data: any) => {
            if (fotoSelecionada && data?.id) {
              uploadFoto({ id: data.id, foto: fotoSelecionada }, { onSettled: () => fecharModal() });
            } else {
              fecharModal();
            }
          },
        }
      );
    }
  };

  const handleQuantidadeChange = (ingredienteId: number, quantidade: number) => {
    setIngredientesQuantidades(prev => {
      if (quantidade <= 0) {
        const novo = { ...prev };
        delete novo[ingredienteId];
        return novo;
      }
      return { ...prev, [ingredienteId]: quantidade };
    });
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-neutral-900">Produtos</h1>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
          <SearchBar
            value={termoBusca}
            onChange={setTermoBusca}
            placeholder="Buscar por nome ou descrição..."
            className="w-full sm:w-80"
          />

          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              id="mostrarInativos"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="mostrarInativos" className="text-neutral-700 cursor-pointer">
              Mostrar inativos
            </label>
          </label>

          <Button onClick={abrirModalNovo} variant="primary" size="md" className="whitespace-nowrap">
            Novo Produto
          </Button>
        </div>
      </div>

      {termoBusca && (
        <p className="text-sm text-neutral-600 mb-4">
          {produtosFiltrados.length} produto(s) encontrado(s)
        </p>
      )}

      {isLoading ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Carregando produtos...</p>
        </div>
      ) : produtosFiltrados && produtosFiltrados.length > 0 ? (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider w-16">
                  Foto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Custo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                  Tempo Preparo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {produtosFiltrados.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((produto) => (
                <tr key={produto.id} className={!produto.ativo ? 'bg-neutral-50' : ''}>
                  <td className="px-4 py-3">
                    {produto.urlFoto ? (
                      <img
                        src={getImagemUrl(produto.urlFoto)!}
                        alt={produto.nome}
                        className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-neutral-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 text-xs text-center">
                        Sem foto
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{produto.nome}</div>
                    {produto.sabores && produto.sabores.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {produto.sabores.map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-primary-50 text-primary-600 border border-primary-100 rounded-full text-xs">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <div className="text-sm text-neutral-500">{produto.descricao}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-sm text-neutral-500">{produto.categoriaProdutoNome || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(produto.precoUnitario)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-sm text-neutral-500">{formatCurrency(produto.custo)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                    <div className="text-sm text-neutral-500">{produto.tempoPreparoMinutos} min</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                        produto.ativo ? 'bg-success-50 text-success-700 border-success-200' : 'bg-danger-50 text-danger-700 border-danger-200'
                      }`}
                    >
                      {produto.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => abrirModalEditar(produto)}
                      className="text-primary-600 hover:text-blue-900 mr-4"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => alternarStatus(produto.id)}
                      className={produto.ativo ? 'text-danger-600 hover:text-red-900' : 'text-success-600 hover:text-green-900'}
                    >
                      {produto.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-100 shadow-md p-6">
          <p className="text-neutral-500 text-center py-8">Nenhum produto encontrado</p>
        </div>
      )}

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-neutral-100 shadow-md-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">
              {produtoEditando ? 'Editar Produto' : 'Novo Produto'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Foto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto do Produto</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-colors flex-shrink-0"
                    onClick={() => inputFotoRef.current?.click()}
                  >
                    {previewFoto ? (
                      <img src={previewFoto} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-400 text-xs text-center px-1">Clique para adicionar</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={inputFotoRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFotoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => inputFotoRef.current?.click()}
                      className="text-sm text-primary-600 hover:text-blue-800 underline"
                    >
                      {previewFoto ? 'Trocar foto' : 'Selecionar foto'}
                    </button>
                    {fotoSelecionada && (
                      <p className="text-xs text-neutral-500 mt-1 truncate">{fotoSelecionada.name}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF ou WebP · máx. 10MB</p>
                  </div>
                </div>
              </div>

              <Input
                label="Nome *"
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Bolo de Chocolate"
                onValidate={validateRequired('Nome')}
                validateOnBlur
              />

              <Textarea
                label="Descrição *"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva o produto..."
                rows={3}
                onValidate={combineValidations(
                  validateRequired('Descrição'),
                  validateMinLength(10, 'Descrição')
                )}
                validateOnBlur
                helperText="Mínimo 10 caracteres"
              />

              <Input
                label="Preço Unitário (R$) *"
                type="text"
                value={formData.precoUnitario}
                onChange={(e) => setFormData({ ...formData, precoUnitario: e.target.value.replace(/[^0-9.,]/g, '') })}
                placeholder="0,00"
                onValidate={combineValidations(
                  validateRequired('Preço'),
                  validatePositiveNumber('Preço')
                )}
                validateOnBlur
                helperText="Digite apenas números. Ex: 25.50"
              />

              <Input
                label="Custo (R$) *"
                type="text"
                value={formData.custo}
                onChange={(e) => setFormData({ ...formData, custo: e.target.value.replace(/[^0-9.,]/g, '') })}
                placeholder="0,00"
                onValidate={combineValidations(
                  validateRequired('Custo'),
                  validatePositiveNumber('Custo')
                )}
                validateOnBlur
                helperText="Custo de produção. Ex: 15.00"
              />

              <Input
                label="Tempo de Preparo (minutos) *"
                type="text"
                value={formData.tempoPreparoMinutos}
                onChange={(e) => setFormData({ ...formData, tempoPreparoMinutos: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="0"
                onValidate={combineValidations(
                  validateRequired('Tempo de preparo'),
                  validateInteger('Tempo de preparo')
                )}
                validateOnBlur
                helperText="Tempo médio em minutos"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={categoriaProdutoId ?? ''}
                  onChange={(e) => setCategoriaProdutoId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Sem categoria</option>
                  {categorias?.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sobEncomenda"
                  checked={sobEncomenda}
                  onChange={(e) => setSobEncomenda(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="sobEncomenda" className="text-sm font-medium text-gray-700">
                  Sob Encomenda (não aparecerá no PDV)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sabores disponíveis</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={novoSabor}
                    onChange={(e) => setNovoSabor(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const s = novoSabor.trim();
                        if (s && !sabores.some(x => x.toLowerCase() === s.toLowerCase())) {
                          setSabores([...sabores, s]);
                          setNovoSabor('');
                        }
                      }
                    }}
                    placeholder="Ex: Chocolate, Morango..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const s = novoSabor.trim();
                      if (s && !sabores.some(x => x.toLowerCase() === s.toLowerCase())) {
                        setSabores([...sabores, s]);
                        setNovoSabor('');
                      }
                    }}
                    className="px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                  >
                    Adicionar
                  </button>
                </div>
                {sabores.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {sabores.map((sabor, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-700 border border-primary-200 rounded-full text-xs">
                        {sabor}
                        <button
                          type="button"
                          onClick={() => setSabores(sabores.filter((_, i) => i !== idx))}
                          className="text-primary-400 hover:text-primary-700 font-bold leading-none"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ingredientes (quantidade necessária por unidade)
                </label>
                {carregandoIngredientes ? (
                  <p className="text-sm text-neutral-500">Carregando ingredientes...</p>
                ) : ingredientes && ingredientes.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-3">
                    {ingredientes.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')).map((ingrediente) => (
                      <div key={ingrediente.id} className="flex items-center gap-3 p-2 hover:bg-neutral-50 rounded">
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-700">{ingrediente.nome}</span>
                          <span className="text-xs text-neutral-500 ml-2">
                            (Estoque: {ingrediente.quantidadeEstoque} {ingrediente.unidadeMedidaSigla})
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="0.000"
                            value={ingredientesDisplay[ingrediente.id] ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^0-9.,]/g, '');
                              setIngredientesDisplay(prev => ({ ...prev, [ingrediente.id]: raw }));
                              handleQuantidadeChange(ingrediente.id, parseFloat(raw.replace(',', '.')) || 0);
                            }}
                            className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-sm text-neutral-600">{ingrediente.unidadeMedidaSigla}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500">Nenhum ingrediente cadastrado</p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={criando || atualizando || enviandoFoto}
                >
                  {criando || atualizando || enviandoFoto ? 'Salvando...' : produtoEditando ? 'Atualizar' : 'Criar'}
                </Button>
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                  fullWidth
                  disabled={criando || atualizando || enviandoFoto}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produtos;
